import {useMealkitCart} from "./useMealkitCart";
import Preloader from "../Preloader";
import RecipeCard from "../v2/recipe/RecipeCard";
import React, {useEffect, useMemo} from "react";
import {WeekDate} from "../v2/weekDate";
import {useWeekRecipes} from "../v2/recipe/useWeekRecipes";
import {getNumberOfMeals, getProductVariant} from "../accountV2/common";
import {BottomBar} from "../v2/recipe/WeekRecipes";
import {isMobile, MAX_RECIPES_FOR_SUBSCRIPTION, MIN_RECIPES_FOR_SUBSCRIPTION} from "../helper";
import {Col, Container, Row} from "react-bootstrap";
import LogoIcon from "../icons/Logo";
import CheckoutNavigationItem from "./CheckoutNavigationItem";
import ButtonWithCounter from "../v2/component/buttonWithCounter/ButtonWithCounter";
import CustomerSubscriptionInfoBar from "./CustomerSubscriptionInfoBar";
import CartOverlay from "../v2/cart/CartOverlay";
import {useFullScreenModal} from "../v2/component/useFullScreenModal";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

const AddonsSelection = (): React.ReactElement => {
  const {t} = useTranslation();
  const cart = useMealkitCart();
  const order = cart.asOrder();
  const {addons: availableAddons, isLoading: isLoadingAddons} = useWeekRecipes(WeekDate(cart.deliveryDate));

  const addRecipe = async (recipeId: string) => await cart.addProduct(recipeId, await cart.getSellingPlanId());
  const removeRecipe = async (recipeId: string) => await cart.removeProduct(recipeId);
  const addAddon = async (productId: string) => await cart.addProduct(productId);
  const removeAddon = async (productId: string) => await cart.removeProduct(productId);

  useEffect(() => {
    window.scrollTo(0, 0);

    [
      document.querySelector(".tt-desktop-header"),
      document.querySelector(".tt-mobile-header"),
    ].forEach((it: HTMLElement) => {
      if (it?.style) {
        it.style.display = "none";
      }
    });
  }, []);

  const onClickIsDisabled = useMemo(() => {
    if (cart.status !== "idle") {
      return true;
    }

    const allMealsInOrder = order.products;
    const recipesInOrder = order.products?.filter(it => !availableAddons?.some(addon => addon.id === it.productId));

    return getNumberOfMeals(recipesInOrder) < MIN_RECIPES_FOR_SUBSCRIPTION ||
      getNumberOfMeals(allMealsInOrder) > MAX_RECIPES_FOR_SUBSCRIPTION;
  }, [order.products, availableAddons, cart.status]);

  const {
    isOpen: cartOverlayIsOpen,
    openModal: openCartOverlay,
    closeModal: hideCartOverlay,
  } = useFullScreenModal("cart");

  const navigate = useNavigate();

  const onContinueFromCart = async () => {
    if (onClickIsDisabled) {
      return;
    }

    hideCartOverlay();
    await cart.proceedToCheckout();
  };

  return <>
    {isLoadingAddons ? (
      <div className="d-flex justify-content-center">
        <Preloader />
      </div>
    ) : (
      <>
        <div style={{overflow: "hidden"}}>
          <CartOverlay order={{...order, totalDiscount: cart.totalDiscountAmount}}
                       isOpen={cartOverlayIsOpen}
                       hide={hideCartOverlay}
                       addRecipe={addRecipe}
                       removeRecipe={removeRecipe}
                       addAddon={addAddon}
                       removeAddon={removeAddon}
                       onRecipeAdd={() => navigate("../boxes")}
                       onAddonAdd={hideCartOverlay}
                       onContinue={onContinueFromCart}
                       withNumberOfPeopleSelection
                       setNumberOfPeople={cart.saveNumberOfPeople}
                       isCheckoutFlow={true}
          />
        </div>
        <div className="sticky-top shadow-sm" style={{backgroundColor: "#FFF", overflow: "hidden auto"}}>
          <Container>
            <Row className="py-2 align-items-center">
              <Col xs={"auto"}>
                <LogoIcon width={isMobile() ? 30 : 40} height={isMobile() ? 30 : 40}
                          onClick={() => window.location.replace("https://www.cleankitchen.ee")}
                />
              </Col>
              <Col className="d-flex justify-content-evenly align-items-center">
                <CheckoutNavigationItem index={1} isActive onClick={() => navigate("../boxes")}>
                  {t("order.first_order_flow.recipes_step")}
                </CheckoutNavigationItem>
                <CheckoutNavigationItem index={2} isActive>
                  {t("order.first_order_flow.addons_step")}
                </CheckoutNavigationItem>
                <CheckoutNavigationItem index={3} onClick={onClickIsDisabled ? undefined : cart.proceedToCheckout}>
                  {t("order.first_order_flow.delivery_and_payment_step")}
                </CheckoutNavigationItem>
              </Col>
              <Col xs={"auto"} className="d-flex justify-content-end fs-4">
                <ButtonWithCounter label={"🛒"} counterValue={cart.numberOfMeals} onClick={openCartOverlay} />
              </Col>
            </Row>
          </Container>
          <Container className="pb-1">
            <CustomerSubscriptionInfoBar />
          </Container>
        </div>
        <div className="pt-2" style={{backgroundColor: "#FFF"}}>
          <Container>
            <Row>
              {availableAddons?.map(addon => {
                // Addons are, at the moment, always paid extra and all variants should have same prices
                // This code still gets the "current" variant, but assumes that every addons is paid for extra
                const currentVariant = getProductVariant(cart.numberOfPeople, cart.numberOfSubscribedRecipes, addon);
                // Addon price is not displayed "per portion"
                const surcharge = Number(currentVariant.price.amount);

                return <RecipeCard
                  key={addon.id}
                  recipe={addon}
                  addRecipe={addAddon}
                  removeRecipe={removeAddon}
                  order={cart.asOrder()}
                  surcharge={surcharge}
                  displayAsSurchargePerPortion={false}
                  displaySurchargeAsDifference={false}
                />;
              })}
            </Row>
          </Container>
        </div>
        {
          cart.isUninitialized && cart.state.cost?.totalAmount ? <></> :
            <BottomBar grossOrderTotal={Number(cart.state.cost?.subtotalAmount?.amount)}
                       discountCodes={cart.discountCodes}
                       totalDiscountAmount={cart.totalDiscountAmount} addDiscount={cart.addDiscount}
                       removeDiscount={cart.removeDiscount}
                       continueIsDisabled={onClickIsDisabled} onClick={cart.proceedToCheckout}
                       continueButtonText={t("recipes.actions.confirm_selection")}
            />
        }
      </>
    )}
  </>;
};

export default AddonsSelection;
